import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  CircularProgress,
  Typography,
  Chip,
  LinearProgress,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const PreviousAssetsModal = ({
  open,
  onClose,
  assetType,
  publicationId,
  publicationVersions = null,
  isLoadingAssets = false,
  onReviewContent,   // (pptId) => void — opens SlideEditorView
  onViewPpt,         // (pptId, versionNumber) => void — opens /ppt/{id}?versionNumber=x
  onRegenerate,      // (pptId) => void — calls /regenerate-ppt
  onResume,          // (pptId) => void — fetches sections and reopens dialog at Step 2
  onReviewContentVideo,
  onViewVideo,
  onRegenerateVideo,
  onResumeVideo,
  onViewPoster,
  onRegeneratePoster,
  onViewAudio,
  onRegenerateAudio,
  taskStatus = {},
  rawTaskStatus = {},
  currentPublicationTaskStatus = {},
}) => {
  const loading = isLoadingAssets;
  // rows: flat list of { project_id, professional_type, status, isCompleted, versionNumber }
  const [rows, setRows] = useState([]);

  const [selectedVersions, setSelectedVersions] = useState({}); // { [rowId]: { project_id, versionNumber, displayVersion } }

  const buildRows = (data) => {
    const types = ["ppt", "video", "poster", "audio"];
    const flatRows = [];

    types.forEach(type => {
      const groups = (data.versions || []).filter((v) => v.type === type);

      groups.forEach((group) => {
        const profType = group.professional_type || "—";
        const projects = Array.isArray(group.projects) ? group.projects : [];
        const versions = Array.isArray(group.versions) ? group.versions : [];

        for (const proj of projects) {
          flatRows.push({
            id: `inprog-${proj.project_id}`,
            type: type,
            project_id: proj.project_id,
            professional_type: profType,
            status: typeof proj.status === "string" ? proj.status : "",
            length: proj.length || null,
            created_at: proj.created_at || null,
            updated_at: proj.updated_at || null,
            isCompleted: false,
            versionNumber: null,
            displayVersion: null,
            timeLabel: proj.updated_at || proj.created_at || group.created_at,
          });
        }

        if (versions.length > 0) {
          versions.sort((a, b) => new Date(a.version_created_at || a.project_created_at || a.created_at || 0).getTime() - new Date(b.version_created_at || b.project_created_at || b.created_at || 0).getTime());
          const latestVersion = versions[versions.length - 1];
          flatRows.push({
            id: `comp-${latestVersion.project_id}`,
            type: type,
            project_id: latestVersion.project_id,
            professional_type: profType,
            status: latestVersion.status || "completed",
            isCompleted: true,
            versionNumber: latestVersion.version_number,
            displayVersion: Number(latestVersion.display_version || versions.length),
            created_at: latestVersion.version_created_at || latestVersion.project_created_at || latestVersion.created_at,
            length: latestVersion.length || null,
            allVersions: versions,
            timeLabel: latestVersion.version_created_at || latestVersion.project_created_at || latestVersion.created_at || group.created_at,
          });
        }
      });
    });

    flatRows.sort((a, b) => {
      const timeA = a.timeLabel ? new Date(a.timeLabel).getTime() : 0;
      const timeB = b.timeLabel ? new Date(b.timeLabel).getTime() : 0;
      return timeB - timeA;
    });

    return flatRows;
  };

  useEffect(() => {
    if (!open) return;
    if (publicationVersions) {
      setRows(buildRows(publicationVersions));
    } else {
      setRows([]);
    }
  }, [open, publicationVersions]);

  const handleClose = () => {
    setSelectedVersions({});
    onClose();
  };

  const getStatusChip = (row, displayStatus) => {
    let mergingLabel = "Creating...";
    if (row?.type) {
      if (row.type.toLowerCase() === "ppt") {
        mergingLabel = "Creating PPT";
      } else if (row.type.toLowerCase() === "video") {
        mergingLabel = "Creating Video";
      } else {
        mergingLabel = `Creating ${row.type.charAt(0).toUpperCase() + row.type.slice(1)}`;
      }
    }

    const map = {
      queued: { label: "Generating sections", color: "default" },
      generating: { label: "Generating...", color: "info" },
      processing: { label: "Review content", sx: { backgroundColor: "#f9a825", color: "#fff" } },
      merging: { label: mergingLabel, sx: { backgroundColor: "#f9a825", color: "#fff" } },
      completed: { label: "Completed", color: "success" },
      failed: { label: "Failed", sx: { backgroundColor: "#ef5350", color: "#fff" } },
      TemplatesSelected: { label: "Generate content", sx: { backgroundColor: "#1e4de3", color: "#fff" } },
    };
    const cfg = map[displayStatus] || { label: displayStatus || "Unknown", color: "default" };
    return cfg.sx ? <Chip label={cfg.label} sx={cfg.sx} size="small" /> : <Chip label={cfg.label} color={cfg.color} size="small" />;
  };

  const getActionButton = (row, displayStatus, progress, isLiveTask) => {
    const { project_id, id } = row;

    if (displayStatus === "completed") {
      const selectedVersionNumber = selectedVersions[id]?.versionNumber || row.versionNumber;
      const selectedDisplayVersion = selectedVersions[id]?.displayVersion || row.displayVersion;
      return (
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            if (row.type === "ppt") {
              onViewPpt(project_id, selectedDisplayVersion, row.allVersions, row.professional_type);
            } else if (row.type === "video") {
              onViewVideo(project_id, selectedVersionNumber, row.allVersions, selectedDisplayVersion, row.professional_type);
            } else if (row.type === "poster") {
              onViewPoster(project_id);
            } else if (row.type === "audio") {
              onViewAudio(project_id);
            }
          }}
          sx={{ backgroundColor: "#2e7d32", color: "#fff", "&:hover": { backgroundColor: "#1b5e20" } }}
        >
          {row.type === "ppt" ? "View PPT" : row.type === "video" ? "Play Video" : row.type === "poster" ? "View Poster" : "Play Audio"}
        </Button>
      );
    }

    if (displayStatus === "failed") {
      return (
        <Button
          variant="contained"
          size="small"
          endIcon={<RefreshIcon />}
          onClick={() => {
            if (row.type === "ppt") onRegenerate(project_id);
            else if (row.type === "video") onRegenerateVideo(project_id);
            else if (row.type === "poster") onRegeneratePoster(project_id);
            else if (row.type === "audio") onRegenerateAudio(project_id);
          }}
          sx={{ backgroundColor: "#ef5350", color: "#fff", "&:hover": { backgroundColor: "#e53935" } }}
        >
          Retry
        </Button>
      );
    }

    if (displayStatus === "TemplatesSelected") {
      return (
        <Button
          variant="outlined"
          size="small"
          endIcon={<ArrowForwardIcon />}
          onClick={() => row.type === "ppt" ? onResume(project_id) : onResumeVideo(project_id)}
          sx={{ color: "#1e4de3", borderColor: "#1e4de3", "&:hover": { borderColor: "#1e4de3", backgroundColor: "rgba(30, 77, 227, 0.04)" } }}
        >
          CONTINUE
        </Button>
      );
    }

    if (displayStatus === "processing" || displayStatus === "merging") {
      return (
        <Button
          variant="contained"
          size="small"
          onClick={() => row.type === "ppt" ? onReviewContent(project_id, row.professional_type) : onReviewContentVideo(project_id, row.professional_type)}
          sx={{ backgroundColor: "#f9a825", color: "#fff", "&:hover": { backgroundColor: "#f57f17" } }}
        >
          Review Content
        </Button>
      );
    }

    if (displayStatus === "generating" || displayStatus === "queued" || (isLiveTask && progress < 100)) {
      return (
        <div style={{ width: "100%", paddingRight: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            <Typography variant="caption" color="text.secondary">
              {displayStatus === "queued" ? "Generating sections..." : "Generating..."}
            </Typography>
            {isLiveTask && progress > 0 && (
              <Typography variant="caption" color="text.secondary">{progress}%</Typography>
            )}
          </div>
          <LinearProgress
            variant={isLiveTask && progress > 0 ? "determinate" : "indeterminate"}
            value={progress}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </div>
      );
    }

    return null;
  };

  const filteredRows = rows.filter((r) => r.type === assetType);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Previous Assets</Typography>
        <IconButton aria-label="close" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
            <CircularProgress />
          </div>
        ) : filteredRows.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
            No previous assets found.
          </div>
        ) : (
          <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '6px 8px' } }}>
            <TableHead>
              <TableRow>
                <TableCell><strong>Audience</strong></TableCell>
                {assetType === 'ppt' && <TableCell><strong>Length</strong></TableCell>}
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Created At</strong></TableCell>
                <TableCell><strong>Completed At</strong></TableCell>
                <TableCell><strong>Version</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row, idx) => {
                const rawStatus = row.status || "";
                let normalizedStatus = rawStatus === "TemplatesSelected" ? "TemplatesSelected" : rawStatus.toLowerCase().replace(/[_\s]/g, "");
                
                if (normalizedStatus === "error" || normalizedStatus === "cancelled" || normalizedStatus === "terminated") normalizedStatus = "failed";
                if (normalizedStatus === "inprogress") normalizedStatus = "generating";
                
                const displayStatus = normalizedStatus;

                let progress = 0;
                let isLiveTask = false;

                if (!row.isCompleted && (displayStatus === "queued" || displayStatus === "generating")) {
                  isLiveTask = true;
                  
                  const mergeTaskKey = row.type + "_merge";
                  const templatesTaskKey = row.type === "ppt" ? "templates_llm" : row.type + "_templates_llm";

                  const findTask = (taskDict) => {
                    if (!taskDict) return null;
                    let genericTask = null;
                    for (const key in taskDict) {
                      const t = taskDict[key];
                      if (!t) continue;
                      
                      if (String(t.project_id) === String(row.project_id) || 
                          String(t.id) === String(row.project_id) || 
                          String(t.pptId) === String(row.project_id) || 
                          String(t.video_project_id) === String(row.project_id) ||
                          String(t.videoProjectId) === String(row.project_id) ||
                          String(t.posterId) === String(row.project_id) ||
                          String(t.carouselId) === String(row.project_id) ||
                          String(t.instapostId) === String(row.project_id) ||
                          String(key) === String(row.project_id)) {
                        return t;
                      }
                      
                      const hasSpecificId = t.pptId || t.video_project_id || t.videoProjectId || t.posterId || t.audioId || t.carouselId || t.instapostId || t.project_id || t.id;
                      if (!hasSpecificId && Number(t.publicationId) === Number(publicationId)) {
                        genericTask = t;
                      }
                    }
                    return genericTask;
                  };

                  const specificTask = findTask(taskStatus[row.type]) || findTask(rawTaskStatus[row.type]);
                  const mergeTask = findTask(taskStatus[mergeTaskKey]) || findTask(rawTaskStatus[mergeTaskKey]);
                  const templateTask = findTask(taskStatus[templatesTaskKey]) || findTask(rawTaskStatus[templatesTaskKey]);

                  let activeTaskStatus = null;
                  if (displayStatus === "queued") {
                    activeTaskStatus = templateTask || specificTask;
                  } else if (displayStatus === "generating") {
                    activeTaskStatus = specificTask || templateTask || mergeTask;
                  } else if (displayStatus === "merging") {
                    activeTaskStatus = mergeTask || specificTask;
                  } else {
                    activeTaskStatus = mergeTask || templateTask || specificTask;
                  }
                  
                  if (activeTaskStatus && activeTaskStatus.progress) {
                    progress = activeTaskStatus.progress;
                  }
                }

                // Dynamically extract data based on the dropdown's selected version (for completed rows)
                const activeSelectedVersion = row.allVersions?.length > 0 && selectedVersions[row.id]
                  ? row.allVersions.find((v, i) => Number(v.display_version || (i + 1)) === Number(selectedVersions[row.id].displayVersion))
                  : (row.allVersions?.length > 0 ? row.allVersions[row.allVersions.length - 1] : null);

                const currentLength = row.allVersions?.length > 0 && activeSelectedVersion ? activeSelectedVersion.length : row.length;
                const currentCreatedAt = row.allVersions?.length > 0 && activeSelectedVersion ? activeSelectedVersion.project_created_at : row.created_at;
                const currentCompletedAt = row.allVersions?.length > 0 && activeSelectedVersion ? activeSelectedVersion.version_created_at : (row.updated_at || row.timeLabel);

                return (
                  <TableRow key={`${row.project_id}-${idx}`} hover>
                    <TableCell>{row.professional_type}</TableCell>

                    {/* Length column — PPT only */}
                    {assetType === 'ppt' && (
                      <TableCell>
                        <Typography variant="body2">{currentLength || "—"}</Typography>
                      </TableCell>
                    )}

                    <TableCell sx={{ minWidth: 120 }}>
                      {getStatusChip(row, displayStatus, progress, isLiveTask)}
                    </TableCell>

                    {/* Created At column */}
                    <TableCell sx={{ minWidth: 100, whiteSpace: "nowrap" }}>
                      <Typography variant="body2" color="text.secondary">
                        {currentCreatedAt
                          ? new Date(currentCreatedAt).toLocaleDateString()
                          : "—"}
                      </Typography>
                    </TableCell>

                    {/* Completed At column — shows "—" if updated_at is within 1 second of created_at */}
                    <TableCell sx={{ minWidth: 100, whiteSpace: "nowrap" }}>
                      <Typography variant="body2" color="text.secondary">
                        {(() => {
                          if (!currentCompletedAt && !currentCreatedAt) return "—";
                          if (!currentCompletedAt) return "—";

                          const createdTime = currentCreatedAt ? new Date(currentCreatedAt).getTime() : 0;
                          const completedTime = new Date(currentCompletedAt).getTime();
                          return (completedTime > createdTime + 1000)
                            ? new Date(currentCompletedAt).toLocaleDateString()
                            : "—";
                        })()}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {row.allVersions?.length > 0 ? (
                        <Select
                          size="small"
                          value={selectedVersions[row.id]?.displayVersion || row.displayVersion}
                          onChange={(e) => {
                            const newDisplayVersion = Number(e.target.value);
                            const targetVersion = row.allVersions.find((v, i) => Number(v.display_version || (i + 1)) === newDisplayVersion) || row.allVersions[row.allVersions.length - 1];
                            const newVersionNumber = targetVersion.version_number || newDisplayVersion;
                            setSelectedVersions(prev => ({
                              ...prev,
                              [row.id]: {
                                displayVersion: newDisplayVersion,
                                versionNumber: newVersionNumber,
                                project_id: targetVersion.project_id
                              }
                            }));
                          }}
                          sx={{ minWidth: 100, height: 32 }}
                        >
                          {row.allVersions.map((v, i) => {
                            const vLabel = Number(v.display_version || (i + 1));
                            return (
                              <MenuItem key={vLabel} value={vLabel}>
                                v{vLabel}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      {getActionButton(
                        {
                          ...row,
                          project_id: row.allVersions?.length > 0 ? (selectedVersions[row.id]?.project_id || row.project_id) : row.project_id,
                          displayVersion: row.allVersions?.length > 0 ? (selectedVersions[row.id]?.displayVersion || row.displayVersion) : row.displayVersion,
                          versionNumber: row.allVersions?.length > 0 ? (selectedVersions[row.id]?.versionNumber || row.versionNumber) : row.versionNumber
                        },
                        displayStatus,
                        progress,
                        isLiveTask
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PreviousAssetsModal;
